import React, { useState, useEffect } from 'react';
import api from '../../services/api';

const AIPromptAssistant = ({ onSelectPrompt, onClose, initialTopic = '' }) => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [generatedPrompts, setGeneratedPrompts] = useState([]);
  const [selectedPrompt, setSelectedPrompt] = useState(null);
  
  // Configuration data
  const [useCases, setUseCases] = useState([]);
  const [moods, setMoods] = useState([]);
  const [styles, setStyles] = useState([]);
  const [platforms, setPlatforms] = useState([]);
  
  // Form data
  const [formData, setFormData] = useState({
    use_case: '',
    platform: '',
    topic: initialTopic,
    mood: '',
    style_preference: '',
    brand_colors: [],
    include_text: '',
    quantity: 3,
    additional_context: '',
    target_audience: '',
    brand_name: ''
  });

  const [colorInput, setColorInput] = useState('');

  // Fetch configuration options on mount
  useEffect(() => {
    fetchConfigurations();
  }, []);

  const fetchConfigurations = async () => {
    try {
      const [useCasesRes, moodsRes, stylesRes, platformsRes] = await Promise.all([
        api.get('/prompts/use-cases'),
        api.get('/prompts/moods'),
        api.get('/prompts/styles'),
        api.get('/prompts/platforms')
      ]);
      
      setUseCases(useCasesRes.data.use_cases || []);
      setMoods(moodsRes.data.moods || []);
      setStyles(stylesRes.data.styles || []);
      setPlatforms(platformsRes.data.platforms || []);
    } catch (err) {
      console.error('Error fetching configurations:', err);
      // Set defaults if API fails
      setUseCases([
        { id: 'social_post', name: 'Social Media Post', description: 'Images for social media' },
        { id: 'blog_header', name: 'Blog Header', description: 'Wide format blog images' },
        { id: 'landing_hero', name: 'Landing Page Hero', description: 'Hero section images' },
        { id: 'ad_creative', name: 'Ad Creative', description: 'Advertising images' },
        { id: 'product_showcase', name: 'Product Showcase', description: 'Product photography' }
      ]);
      setMoods([
        { id: 'professional', name: 'Professional' },
        { id: 'playful', name: 'Playful' },
        { id: 'inspiring', name: 'Inspiring' },
        { id: 'minimalist', name: 'Minimalist' }
      ]);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleAddColor = () => {
    if (colorInput && !formData.brand_colors.includes(colorInput)) {
      setFormData(prev => ({
        ...prev,
        brand_colors: [...prev.brand_colors, colorInput]
      }));
      setColorInput('');
    }
  };

  const handleRemoveColor = (color) => {
    setFormData(prev => ({
      ...prev,
      brand_colors: prev.brand_colors.filter(c => c !== color)
    }));
  };

  const handleGeneratePrompts = async () => {
    if (!formData.topic.trim()) {
      setError('Please enter a topic or description');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await api.post('/prompts/generate', formData);
      setGeneratedPrompts(response.data.prompts || []);
      setStep(5); // Move to results step
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to generate prompts');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectPrompt = (prompt) => {
    setSelectedPrompt(prompt);
    if (onSelectPrompt) {
      onSelectPrompt({
        prompt: prompt.prompt,
        negative_prompt: prompt.negative_prompt,
        width: prompt.recommended_dimensions.width,
        height: prompt.recommended_dimensions.height,
        style_preset: prompt.style_preset
      });
    }
  };

  const handleQuickGenerate = async () => {
    if (!formData.topic.trim()) {
      setError('Please enter a topic');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await api.post('/prompts/generate', {
        ...formData,
        use_case: formData.use_case || 'social_post',
        quantity: 1
      });
      
      if (response.data.prompts?.length > 0) {
        handleSelectPrompt(response.data.prompts[0]);
        if (onClose) onClose();
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to generate prompt');
    } finally {
      setLoading(false);
    }
  };

  const nextStep = () => setStep(prev => Math.min(prev + 1, 5));
  const prevStep = () => setStep(prev => Math.max(prev - 1, 1));

  const getSelectedUseCase = () => useCases.find(u => u.id === formData.use_case);
  const getAvailablePlatforms = () => {
    const useCase = getSelectedUseCase();
    if (useCase?.platforms?.length) {
      return platforms.filter(p => useCase.platforms.includes(p.id));
    }
    return platforms;
  };

  const styles_css = {
    overlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '20px'
    },
    modal: {
      backgroundColor: 'var(--bg-secondary)',
      borderRadius: '16px',
      border: '1px solid var(--border-color)',
      width: '100%',
      maxWidth: '600px',
      maxHeight: '90vh',
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column'
    },
    header: {
      padding: '20px 24px',
      borderBottom: '1px solid var(--border-color)',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center'
    },
    headerTitle: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px'
    },
    title: {
      fontSize: '18px',
      fontWeight: '600',
      color: 'var(--text-primary)',
      margin: 0
    },
    closeBtn: {
      background: 'none',
      border: 'none',
      fontSize: '24px',
      color: 'var(--text-secondary)',
      cursor: 'pointer',
      padding: '4px'
    },
    content: {
      padding: '24px',
      overflowY: 'auto',
      flex: 1
    },
    stepIndicator: {
      display: 'flex',
      justifyContent: 'center',
      gap: '8px',
      marginBottom: '24px'
    },
    stepDot: {
      width: '10px',
      height: '10px',
      borderRadius: '50%',
      backgroundColor: 'var(--border-color)',
      transition: 'all 0.2s'
    },
    stepDotActive: {
      backgroundColor: 'var(--primary-color)',
      transform: 'scale(1.2)'
    },
    stepDotCompleted: {
      backgroundColor: 'var(--success-color, #22c55e)'
    },
    stepTitle: {
      fontSize: '16px',
      fontWeight: '600',
      color: 'var(--text-primary)',
      marginBottom: '16px'
    },
    stepDescription: {
      fontSize: '14px',
      color: 'var(--text-secondary)',
      marginBottom: '20px'
    },
    optionsGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(2, 1fr)',
      gap: '12px'
    },
    optionCard: {
      padding: '16px',
      borderRadius: '12px',
      border: '1px solid var(--border-color)',
      backgroundColor: 'var(--bg-primary)',
      cursor: 'pointer',
      transition: 'all 0.2s',
      textAlign: 'left'
    },
    optionCardSelected: {
      borderColor: 'var(--primary-color)',
      backgroundColor: 'rgba(var(--primary-rgb), 0.05)',
      boxShadow: '0 0 0 2px rgba(var(--primary-rgb), 0.1)'
    },
    optionName: {
      fontSize: '14px',
      fontWeight: '500',
      color: 'var(--text-primary)',
      marginBottom: '4px'
    },
    optionDescription: {
      fontSize: '12px',
      color: 'var(--text-tertiary)'
    },
    formGroup: {
      marginBottom: '20px'
    },
    label: {
      display: 'block',
      fontSize: '13px',
      fontWeight: '600',
      color: 'var(--text-primary)',
      marginBottom: '8px'
    },
    labelHint: {
      fontWeight: '400',
      color: 'var(--text-tertiary)',
      marginLeft: '4px'
    },
    input: {
      width: '100%',
      padding: '12px',
      borderRadius: '8px',
      border: '1px solid var(--border-color)',
      backgroundColor: 'var(--bg-primary)',
      color: 'var(--text-primary)',
      fontSize: '14px',
      outline: 'none'
    },
    textarea: {
      width: '100%',
      padding: '12px',
      borderRadius: '8px',
      border: '1px solid var(--border-color)',
      backgroundColor: 'var(--bg-primary)',
      color: 'var(--text-primary)',
      fontSize: '14px',
      outline: 'none',
      resize: 'vertical',
      minHeight: '100px',
      fontFamily: 'inherit'
    },
    colorInputWrapper: {
      display: 'flex',
      gap: '8px'
    },
    colorPreview: {
      width: '40px',
      height: '40px',
      borderRadius: '8px',
      border: '1px solid var(--border-color)'
    },
    colorTags: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: '8px',
      marginTop: '8px'
    },
    colorTag: {
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
      padding: '4px 8px',
      borderRadius: '6px',
      backgroundColor: 'var(--bg-primary)',
      border: '1px solid var(--border-color)',
      fontSize: '12px'
    },
    colorSwatch: {
      width: '14px',
      height: '14px',
      borderRadius: '3px'
    },
    colorRemoveBtn: {
      background: 'none',
      border: 'none',
      color: 'var(--text-tertiary)',
      cursor: 'pointer',
      fontSize: '14px',
      padding: 0
    },
    footer: {
      padding: '16px 24px',
      borderTop: '1px solid var(--border-color)',
      display: 'flex',
      justifyContent: 'space-between',
      gap: '12px'
    },
    btn: {
      padding: '10px 20px',
      borderRadius: '8px',
      fontSize: '14px',
      fontWeight: '500',
      cursor: 'pointer',
      transition: 'all 0.2s',
      display: 'flex',
      alignItems: 'center',
      gap: '6px'
    },
    btnPrimary: {
      backgroundColor: 'var(--primary-color)',
      color: 'white',
      border: 'none'
    },
    btnSecondary: {
      backgroundColor: 'transparent',
      color: 'var(--text-secondary)',
      border: '1px solid var(--border-color)'
    },
    btnDisabled: {
      opacity: 0.5,
      cursor: 'not-allowed'
    },
    alert: {
      padding: '12px 16px',
      borderRadius: '8px',
      marginBottom: '16px',
      fontSize: '14px',
      backgroundColor: 'rgba(239, 68, 68, 0.1)',
      color: '#ef4444',
      border: '1px solid rgba(239, 68, 68, 0.2)'
    },
    promptCard: {
      padding: '16px',
      borderRadius: '12px',
      border: '1px solid var(--border-color)',
      backgroundColor: 'var(--bg-primary)',
      marginBottom: '12px',
      cursor: 'pointer',
      transition: 'all 0.2s'
    },
    promptCardSelected: {
      borderColor: 'var(--primary-color)',
      boxShadow: '0 0 0 2px rgba(var(--primary-rgb), 0.1)'
    },
    promptText: {
      fontSize: '14px',
      color: 'var(--text-primary)',
      lineHeight: '1.5',
      marginBottom: '12px'
    },
    promptMeta: {
      display: 'flex',
      gap: '16px',
      flexWrap: 'wrap'
    },
    promptMetaItem: {
      fontSize: '12px',
      color: 'var(--text-tertiary)',
      display: 'flex',
      alignItems: 'center',
      gap: '4px'
    },
    promptExplanation: {
      fontSize: '12px',
      color: 'var(--text-secondary)',
      fontStyle: 'italic',
      marginTop: '8px',
      padding: '8px',
      backgroundColor: 'var(--bg-tertiary)',
      borderRadius: '6px'
    },
    quickGenerateSection: {
      padding: '16px',
      backgroundColor: 'var(--bg-tertiary)',
      borderRadius: '12px',
      marginBottom: '20px'
    },
    quickGenerateTitle: {
      fontSize: '14px',
      fontWeight: '600',
      color: 'var(--text-primary)',
      marginBottom: '12px',
      display: 'flex',
      alignItems: 'center',
      gap: '8px'
    },
    quickGenerateRow: {
      display: 'flex',
      gap: '12px'
    },
    spinner: {
      width: '16px',
      height: '16px',
      border: '2px solid rgba(255,255,255,0.3)',
      borderTopColor: 'white',
      borderRadius: '50%',
      animation: 'spin 0.8s linear infinite'
    },
    select: {
      width: '100%',
      padding: '12px',
      borderRadius: '8px',
      border: '1px solid var(--border-color)',
      backgroundColor: 'var(--bg-primary)',
      color: 'var(--text-primary)',
      fontSize: '14px',
      outline: 'none',
      cursor: 'pointer'
    }
  };

  // Render step content
  const renderStepContent = () => {
    switch (step) {
      case 1:
        return (
          <>
            <div style={styles_css.stepTitle}>Select Use Case</div>
            <div style={styles_css.stepDescription}>
              What type of image are you creating?
            </div>
            <div style={styles_css.optionsGrid}>
              {useCases.map(useCase => (
                <div
                  key={useCase.id}
                  style={{
                    ...styles_css.optionCard,
                    ...(formData.use_case === useCase.id ? styles_css.optionCardSelected : {})
                  }}
                  onClick={() => setFormData(prev => ({ ...prev, use_case: useCase.id }))}
                >
                  <div style={styles_css.optionName}>{useCase.name}</div>
                  <div style={styles_css.optionDescription}>{useCase.description}</div>
                </div>
              ))}
            </div>
          </>
        );

      case 2:
        const availablePlatforms = getAvailablePlatforms();
        return (
          <>
            <div style={styles_css.stepTitle}>Select Platform</div>
            <div style={styles_css.stepDescription}>
              Which platform will this image be used on? (Optional)
            </div>
            <div style={styles_css.optionsGrid}>
              <div
                style={{
                  ...styles_css.optionCard,
                  ...(formData.platform === '' ? styles_css.optionCardSelected : {})
                }}
                onClick={() => setFormData(prev => ({ ...prev, platform: '' }))}
              >
                <div style={styles_css.optionName}>General Purpose</div>
                <div style={styles_css.optionDescription}>No specific platform</div>
              </div>
              {availablePlatforms.map(platform => (
                <div
                  key={platform.id}
                  style={{
                    ...styles_css.optionCard,
                    ...(formData.platform === platform.id ? styles_css.optionCardSelected : {})
                  }}
                  onClick={() => setFormData(prev => ({ ...prev, platform: platform.id }))}
                >
                  <div style={styles_css.optionName}>{platform.name}</div>
                  <div style={styles_css.optionDescription}>
                    {platform.formats?.map(f => `${f.dimensions.width}×${f.dimensions.height}`).join(', ')}
                  </div>
                </div>
              ))}
            </div>
          </>
        );

      case 3:
        return (
          <>
            <div style={styles_css.stepTitle}>Describe Your Image</div>
            <div style={styles_css.stepDescription}>
              What should the image show or represent?
            </div>
            
            <div style={styles_css.formGroup}>
              <label style={styles_css.label}>Topic / Description *</label>
              <textarea
                name="topic"
                value={formData.topic}
                onChange={handleInputChange}
                style={styles_css.textarea}
                placeholder="E.g., Professional team collaboration in a modern office space, diverse team members working together..."
                rows={4}
              />
            </div>

            <div style={styles_css.formGroup}>
              <label style={styles_css.label}>
                Additional Context
                <span style={styles_css.labelHint}>(optional)</span>
              </label>
              <input
                type="text"
                name="additional_context"
                value={formData.additional_context}
                onChange={handleInputChange}
                style={styles_css.input}
                placeholder="Any specific details or requirements..."
              />
            </div>

            <div style={styles_css.formGroup}>
              <label style={styles_css.label}>
                Text to Include
                <span style={styles_css.labelHint}>(optional)</span>
              </label>
              <input
                type="text"
                name="include_text"
                value={formData.include_text}
                onChange={handleInputChange}
                style={styles_css.input}
                placeholder="E.g., 'Join Us Today!'"
              />
            </div>
          </>
        );

      case 4:
        return (
          <>
            <div style={styles_css.stepTitle}>Style & Mood</div>
            <div style={styles_css.stepDescription}>
              How should the image feel?
            </div>

            <div style={styles_css.formGroup}>
              <label style={styles_css.label}>Mood</label>
              <div style={styles_css.optionsGrid}>
                {moods.map(mood => (
                  <div
                    key={mood.id}
                    style={{
                      ...styles_css.optionCard,
                      ...(formData.mood === mood.id ? styles_css.optionCardSelected : {}),
                      padding: '12px'
                    }}
                    onClick={() => setFormData(prev => ({ ...prev, mood: mood.id }))}
                  >
                    <div style={styles_css.optionName}>{mood.name}</div>
                  </div>
                ))}
              </div>
            </div>

            <div style={styles_css.formGroup}>
              <label style={styles_css.label}>Style Preference</label>
              <select
                name="style_preference"
                value={formData.style_preference}
                onChange={handleInputChange}
                style={styles_css.select}
              >
                <option value="">Auto-select based on use case</option>
                {styles.map(style => (
                  <option key={style.id} value={style.id}>{style.name}</option>
                ))}
              </select>
            </div>

            <div style={styles_css.formGroup}>
              <label style={styles_css.label}>
                Brand Colors
                <span style={styles_css.labelHint}>(optional)</span>
              </label>
              <div style={styles_css.colorInputWrapper}>
                <input
                  type="color"
                  value={colorInput || '#3B82F6'}
                  onChange={(e) => setColorInput(e.target.value)}
                  style={{ ...styles_css.colorPreview, cursor: 'pointer' }}
                />
                <input
                  type="text"
                  value={colorInput}
                  onChange={(e) => setColorInput(e.target.value)}
                  style={{ ...styles_css.input, flex: 1 }}
                  placeholder="#3B82F6"
                />
                <button
                  style={{ ...styles_css.btn, ...styles_css.btnSecondary }}
                  onClick={handleAddColor}
                  type="button"
                >
                  Add
                </button>
              </div>
              {formData.brand_colors.length > 0 && (
                <div style={styles_css.colorTags}>
                  {formData.brand_colors.map(color => (
                    <div key={color} style={styles_css.colorTag}>
                      <div style={{ ...styles_css.colorSwatch, backgroundColor: color }} />
                      <span>{color}</span>
                      <button
                        style={styles_css.colorRemoveBtn}
                        onClick={() => handleRemoveColor(color)}
                        type="button"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div style={styles_css.formGroup}>
              <label style={styles_css.label}>
                Number of Variations: {formData.quantity}
              </label>
              <input
                type="range"
                name="quantity"
                min="1"
                max="5"
                value={formData.quantity}
                onChange={handleInputChange}
                style={{ width: '100%' }}
              />
            </div>
          </>
        );

      case 5:
        return (
          <>
            <div style={styles_css.stepTitle}>Generated Prompts</div>
            <div style={styles_css.stepDescription}>
              Select a prompt to use, or click to copy
            </div>

            {generatedPrompts.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-tertiary)' }}>
                No prompts generated yet
              </div>
            ) : (
              generatedPrompts.map((prompt, index) => (
                <div
                  key={index}
                  style={{
                    ...styles_css.promptCard,
                    ...(selectedPrompt === prompt ? styles_css.promptCardSelected : {})
                  }}
                  onClick={() => handleSelectPrompt(prompt)}
                >
                  <div style={styles_css.promptText}>
                    {prompt.prompt}
                  </div>
                  <div style={styles_css.promptMeta}>
                    <span style={styles_css.promptMetaItem}>
                      📐 {prompt.recommended_dimensions.width} × {prompt.recommended_dimensions.height}
                    </span>
                    <span style={styles_css.promptMetaItem}>
                      🎨 {prompt.style_preset}
                    </span>
                    {prompt.platform_optimized && (
                      <span style={styles_css.promptMetaItem}>
                        ✓ Platform optimized
                      </span>
                    )}
                  </div>
                  {prompt.explanation && (
                    <div style={styles_css.promptExplanation}>
                      💡 {prompt.explanation}
                    </div>
                  )}
                </div>
              ))
            )}
          </>
        );

      default:
        return null;
    }
  };

  return (
    <div style={styles_css.overlay} onClick={(e) => e.target === e.currentTarget && onClose?.()}>
      <div style={styles_css.modal}>
        {/* Header */}
        <div style={styles_css.header}>
          <div style={styles_css.headerTitle}>
            <span style={{ fontSize: '24px' }}>✨</span>
            <h2 style={styles_css.title}>AI Prompt Assistant</h2>
          </div>
          <button style={styles_css.closeBtn} onClick={onClose}>×</button>
        </div>

        {/* Content */}
        <div style={styles_css.content}>
          {/* Quick Generate */}
          {step === 1 && (
            <div style={styles_css.quickGenerateSection}>
              <div style={styles_css.quickGenerateTitle}>
                ⚡ Quick Generate
              </div>
              <div style={styles_css.quickGenerateRow}>
                <input
                  type="text"
                  value={formData.topic}
                  onChange={(e) => setFormData(prev => ({ ...prev, topic: e.target.value }))}
                  style={{ ...styles_css.input, flex: 1 }}
                  placeholder="Describe what you want..."
                />
                <button
                  style={{
                    ...styles_css.btn,
                    ...styles_css.btnPrimary,
                    ...(loading ? styles_css.btnDisabled : {})
                  }}
                  onClick={handleQuickGenerate}
                  disabled={loading}
                >
                  {loading ? <div style={styles_css.spinner} /> : '⚡'} Generate
                </button>
              </div>
            </div>
          )}

          {/* Error Alert */}
          {error && (
            <div style={styles_css.alert}>
              {error}
            </div>
          )}

          {/* Step Indicator */}
          <div style={styles_css.stepIndicator}>
            {[1, 2, 3, 4, 5].map(s => (
              <div
                key={s}
                style={{
                  ...styles_css.stepDot,
                  ...(s === step ? styles_css.stepDotActive : {}),
                  ...(s < step ? styles_css.stepDotCompleted : {})
                }}
              />
            ))}
          </div>

          {/* Step Content */}
          {renderStepContent()}
        </div>

        {/* Footer */}
        <div style={styles_css.footer}>
          <div>
            {step > 1 && (
              <button
                style={{ ...styles_css.btn, ...styles_css.btnSecondary }}
                onClick={prevStep}
              >
                ← Back
              </button>
            )}
          </div>
          <div style={{ display: 'flex', gap: '12px' }}>
            {step < 5 && (
              <button
                style={{
                  ...styles_css.btn,
                  ...styles_css.btnPrimary,
                  ...(step === 4 && loading ? styles_css.btnDisabled : {})
                }}
                onClick={step === 4 ? handleGeneratePrompts : nextStep}
                disabled={step === 4 && loading}
              >
                {step === 4 ? (
                  loading ? (
                    <>
                      <div style={styles_css.spinner} />
                      Generating...
                    </>
                  ) : (
                    <>✨ Generate Prompts</>
                  )
                ) : (
                  <>Next →</>
                )}
              </button>
            )}
            {step === 5 && selectedPrompt && (
              <button
                style={{ ...styles_css.btn, ...styles_css.btnPrimary }}
                onClick={onClose}
              >
                Use Selected Prompt
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Spinner keyframes */}
      <style>
        {`
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
        `}
      </style>
    </div>
  );
};

export default AIPromptAssistant;
